/**
 * Liquid-glass surface tokens.
 *
 * Every floating surface in the dashboards (dialogs, menus, popovers,
 * drawers) is built from these values, so the whole app can be retuned from
 * one file instead of hunting down per-component `sx` blocks.
 *
 * The thing that took the longest to get right: **glass is barely blurred.**
 * The instinct is to reach for a big blur, but 20-30px turns whatever is
 * behind into an even smear and the panel just reads as a grey box. Real
 * glass keeps the shapes behind it recognisable — you can see that a card is
 * a card — and sells itself with transparency and a lit edge instead. Blur
 * here is single digits on purpose.
 *
 * So the effect is, in order of how much each part matters:
 *
 *   1. Transparency. The surface is mostly see-through; the content behind
 *      stays legible as content.
 *   2. The rim. Layered inset highlights along all four edges, brightest at
 *      the top, so the pane has a lit bevel and visible thickness. This is
 *      what makes it look like a physical pane rather than a faded box.
 *   3. A very low blur plus heavy saturation — enough to soften detail and
 *      pull colour through, not enough to destroy structure. 2px. Really.
 *   4. Something worth refracting behind it (see `backdrop`).
 *
 * Dialogs sit on a dimmed scrim, so they can afford to be very transparent.
 * Menus and popovers do not — their backdrop is invisible so they float
 * directly over live content — which is why `strong` surfaces are both more
 * opaque and more blurred. They are the one place a big blur is correct.
 *
 * ## Two sets, not one set with the colours flipped
 *
 * Dark glass is not light glass inverted. Both sets keep the rim white,
 * because a rim is light catching an edge and light does not turn black when
 * the room does — an inverted rim reads as a drawn border, and the pane goes
 * flat. What changes is how much of it there is: on a dark ground a white
 * edge at full strength glares, so the highlights drop to roughly a fifth of
 * their light-mode alpha and the *bottom* edge goes dark instead, which is
 * what gives the pane its thickness.
 *
 * The scrim inverts properly, though. In light mode it whitens first so the
 * glass always lands on a light field; in dark mode it blackens first, for
 * exactly the same reason in reverse — the surface has to guarantee its own
 * ground, or legibility depends on whatever the page happens to have behind
 * it that day. That bug cost us half of Page settings once already.
 */
import { useTheme } from '@mui/material/styles';

const LIGHT = {
  radius: 24,
  radiusSmall: 16,

  // Scrimmed surfaces (dialogs, drawers): the glass proper.
  blur: 'blur(3px) saturate(210%)',
  // 0.09 was the prettiest value and an unusable one. The panel only tints
  // what is behind it, so over a dark band of the page — a hero section in
  // the page editor, say — the near-black label text landed on a near-black
  // ground and vanished. Half the fields in Page settings were unreadable
  // while the top half looked fine, purely because of what happened to sit
  // behind them. Legibility cannot depend on the page underneath.
  //
  // The panel is still mostly see-through; the lift that makes text safe
  // comes mainly from the scrim below, which now guarantees a light ground
  // rather than merely dimming.
  surface: 'rgba(255, 255, 255, 0.34)',

  // Unscrimmed surfaces (menus, popovers, selects) float over live content,
  // so here the blur does the work of separating the panel from the page.
  blurStrong: 'blur(20px) saturate(180%)',
  surfaceStrong: 'rgba(255, 255, 255, 0.72)',

  // Form controls sitting ON a glass panel. They have to be brighter than the
  // surface to read as interactive, but not so bright they become opaque tiles
  // floating on the pane — the panel is only 0.12, so these stay low too.
  control: 'rgba(255, 255, 255, 0.42)',
  controlFocus: 'rgba(255, 255, 255, 0.55)',
  controlBorder: 'rgba(16, 24, 40, 0.14)',
  controlBorderHover: 'rgba(16, 24, 40, 0.28)',

  // Controls that are themselves glass — buttons, icon buttons. A smaller,
  // tighter version of the panel rim: the bevel has to read at 40px tall, not
  // 600px, so the highlights are thinner and the bloom is shorter.
  rimSmall:
    'inset 0 1px 0 rgba(255, 255, 255, 0.75), ' +
    'inset 0 -1px 0 rgba(16, 24, 40, 0.10), ' +
    'inset 1px 0 0 rgba(255, 255, 255, 0.35), ' +
    'inset -1px 0 0 rgba(255, 255, 255, 0.35), ' +
    'inset 0 6px 10px -8px rgba(255, 255, 255, 0.9)',
  buttonBlur: 'blur(12px) saturate(180%)',
  // Filled buttons keep enough of their colour to carry white text — a
  // primary action has to stay obviously the primary action.
  buttonFillAlpha: 0.76,
  buttonFillAlphaHover: 0.9,
  // Text/outlined buttons sit on the panel as a faint pane of their own.
  buttonQuiet: 'rgba(255, 255, 255, 0.18)',
  buttonQuietHover: 'rgba(255, 255, 255, 0.32)',
  buttonDisabled: 'rgba(255, 255, 255, 0.22)',


  // ── Interactive states ──────────────────────────────────────────────────
  //
  // Hover on a glass control is a *lighting* change, not a colour change:
  // the pane catches more light, the rim brightens, and the specular bloom
  // grows. Repainting the fill a different hue reads as a different button
  // rather than the same button lit differently.
  buttonHoverRim:
    'inset 0 1px 0 rgba(255, 255, 255, 0.95), ' +
    'inset 0 -1px 0 rgba(16, 24, 40, 0.10), ' +
    'inset 1px 0 0 rgba(255, 255, 255, 0.55), ' +
    'inset -1px 0 0 rgba(255, 255, 255, 0.55), ' +
    'inset 0 8px 14px -8px rgba(255, 255, 255, 1)',
  // A short outer bloom so the control lifts off the surface on hover.
  buttonHoverGlow: '0 6px 18px -8px rgba(16, 24, 40, 0.45)',

  // Disabled glass: the light goes out. The pane is still there — you can see
  // its edges — but nothing is lit, so it reads as inert rather than missing.
  // Deliberately NOT `opacity: 0.5` on the whole control: that fades the label
  // into unreadability, and a disabled button still has to say what it does.
  buttonDisabledRim:
    'inset 0 1px 0 rgba(255, 255, 255, 0.30), ' +
    'inset 0 -1px 0 rgba(16, 24, 40, 0.06)',
  buttonDisabledText: 'rgba(16, 24, 40, 0.40)',

  // ── Tab bar (Style 2) ───────────────────────────────────────────────────
  //
  // The active tab is a brand-filled capsule floating in a glass trough.
  // Style 1 (a frosted, colourless pill) loses the selection at a glance on a
  // phone, which is the whole point of the control — so the fill carries the
  // brand colour and the rim keeps it reading as glass rather than as a flat
  // chip.
  tabTrough: 'rgba(255, 255, 255, 0.22)',
  tabTroughBorder: '1px solid rgba(255, 255, 255, 0.55)',
  tabPillRim:
    'inset 0 1px 0 rgba(255, 255, 255, 0.85), ' +
    'inset 0 -1px 0 rgba(16, 24, 40, 0.18), ' +
    'inset 0 10px 16px -10px rgba(255, 255, 255, 0.9)',
  tabPillGlow: '0 8px 20px -10px rgba(66, 42, 251, 0.75)',
  tabPillAlpha: 0.92,
  // The ink on a tab nobody has selected. It sits on the trough, not on the
  // capsule, so it is the one piece of the control that has to follow the
  // mode — and it was the piece left hardcoded.
  tabInk: 'rgba(16, 24, 40, 0.68)',
  tabInkHover: 'rgba(16, 24, 40, 0.92)',

  // Used when the browser cannot blur — see `glassSurface`.
  surfaceFallback: 'rgba(255, 255, 255, 0.97)',

  // A specular bloom in the top-left corner where the light lands. Kept local
  // so the middle of the pane stays genuinely see-through.
  sheen:
    'radial-gradient(120% 90% at 0% 0%, rgba(255,255,255,0.55) 0%, ' +
    'rgba(255,255,255,0.12) 30%, rgba(255,255,255,0) 58%)',

  border: '1px solid rgba(255, 255, 255, 0.70)',
  // A hairline that reads against the surface rather than against the page,
  // for dividers *inside* a glass panel.
  divider: '1px solid rgba(255, 255, 255, 0.40)',

  shadow: '0 34px 70px -22px rgba(16, 24, 40, 0.50)',

  // The lit bevel. Four edge highlights plus a soft bloom inside the top and
  // bottom, so light appears to travel through the thickness of the pane.
  // This is doing most of the work — without it the surface is just faint.
  rim:
    'inset 0 1.5px 1px rgba(255, 255, 255, 1), ' +
    'inset 0 -1.5px 1px rgba(255, 255, 255, 0.70), ' +
    'inset 3px 0 5px -3px rgba(255, 255, 255, 1), ' +
    'inset -3px 0 5px -3px rgba(255, 255, 255, 1), ' +
    'inset 0 16px 30px -20px rgba(255, 255, 255, 1), ' +
    'inset 0 -20px 34px -22px rgba(255, 255, 255, 0.95)',

  // The layer the panel refracts.
  //
  // Two competing needs, and the balance below favours the first:
  //   - You have to be able to SEE the page through the glass. Blurred, but
  //     still recognisably the dashboard. That is what makes it read as glass
  //     rather than as a tinted card.
  //   - The page is white cards on a near-white background, so a purely
  //     neutral scrim blurs white into more white and gives the pane nothing
  //     to pick up.
  // So: a light brand tint for colour, and a modest blur that softens the
  // content behind without erasing it. Pushing the blur high wipes the page
  // out completely and the panel goes back to looking like a grey box.
  // Leads with a white veil, not a dark one. A dark scrim over a light page
  // and a dark scrim over a dark page give very different grounds, and the
  // glass inherits whichever it gets — which is how dark page content ended
  // up showing through dialog labels. Whitening first flattens both cases to
  // a consistently light field, and the brand tint then colours it.
  backdrop:
    'linear-gradient(rgba(255, 255, 255, 0.58), rgba(255, 255, 255, 0.58)), ' +
    'radial-gradient(120% 110% at 18% 0%, rgba(66, 42, 251, 0.16) 0%, ' +
    'rgba(147, 51, 234, 0.11) 36%, rgba(16, 24, 40, 0.20) 100%)',
  // Enough to soften, not enough to erase. Shapes behind must survive — a card
  // should still read as a card through the glass.
  backdropBlur: 'blur(14px)',

  // A pinned bar's own frost (see `glassBar`), and the ink on a tooltip.
  // Both used to be written inline; they are tokens now because they are the
  // two places the dark set needs a different value rather than a fainter one.
  barFill: 'rgba(255, 255, 255, 0.10)',
  tooltipText: 'rgba(16, 24, 40, 0.92)',
};

/**
 * The dark set. Same structure, same names; see the note at the top of the
 * file for what does and does not invert.
 */
const DARK = {
  radius: LIGHT.radius,
  radiusSmall: LIGHT.radiusSmall,

  blur: LIGHT.blur,
  // Darker and a little more opaque than the light surface. A dark pane can
  // afford less transparency than a light one before it stops reading as
  // glass — what sells it here is the rim, not the see-through.
  surface: 'rgba(20, 26, 38, 0.52)',

  blurStrong: LIGHT.blurStrong,
  surfaceStrong: 'rgba(18, 23, 33, 0.82)',

  // Fields on a dark pane are holes, not tiles: a lighter fill reads as
  // something sitting on the surface, a slightly darker one as something cut
  // into it, which is what an input is.
  control: 'rgba(255, 255, 255, 0.06)',
  controlFocus: 'rgba(255, 255, 255, 0.12)',
  controlBorder: 'rgba(255, 255, 255, 0.16)',
  controlBorderHover: 'rgba(255, 255, 255, 0.34)',

  rimSmall:
    'inset 0 1px 0 rgba(255, 255, 255, 0.16), ' +
    'inset 0 -1px 0 rgba(0, 0, 0, 0.45), ' +
    'inset 1px 0 0 rgba(255, 255, 255, 0.07), ' +
    'inset -1px 0 0 rgba(255, 255, 255, 0.07), ' +
    'inset 0 6px 10px -8px rgba(255, 255, 255, 0.20)',
  buttonBlur: LIGHT.buttonBlur,
  buttonFillAlpha: 0.82,
  buttonFillAlphaHover: 0.94,
  buttonQuiet: 'rgba(255, 255, 255, 0.08)',
  buttonQuietHover: 'rgba(255, 255, 255, 0.16)',
  buttonDisabled: 'rgba(255, 255, 255, 0.05)',

  buttonHoverRim:
    'inset 0 1px 0 rgba(255, 255, 255, 0.28), ' +
    'inset 0 -1px 0 rgba(0, 0, 0, 0.45), ' +
    'inset 1px 0 0 rgba(255, 255, 255, 0.12), ' +
    'inset -1px 0 0 rgba(255, 255, 255, 0.12), ' +
    'inset 0 8px 14px -8px rgba(255, 255, 255, 0.30)',
  // The bloom under a lifted control is cast, not emitted, so on a dark
  // ground it is a deeper shadow rather than a brighter halo.
  buttonHoverGlow: '0 6px 18px -8px rgba(0, 0, 0, 0.80)',

  buttonDisabledRim:
    'inset 0 1px 0 rgba(255, 255, 255, 0.08), ' +
    'inset 0 -1px 0 rgba(0, 0, 0, 0.30)',
  buttonDisabledText: 'rgba(230, 234, 242, 0.42)',

  tabTrough: 'rgba(255, 255, 255, 0.06)',
  tabTroughBorder: '1px solid rgba(255, 255, 255, 0.12)',
  tabPillRim:
    'inset 0 1px 0 rgba(255, 255, 255, 0.30), ' +
    'inset 0 -1px 0 rgba(0, 0, 0, 0.50), ' +
    'inset 0 10px 16px -10px rgba(255, 255, 255, 0.35)',
  tabPillGlow: '0 8px 20px -10px rgba(0, 0, 0, 0.90)',
  tabPillAlpha: 0.95,
  // Not a straight inversion of the light value: the dark trough is barely
  // lighter than the panel under it, so an unselected label needs more weight
  // here than its light counterpart to stay comfortably readable.
  tabInk: 'rgba(233, 237, 245, 0.72)',
  tabInkHover: 'rgba(233, 237, 245, 0.96)',

  surfaceFallback: 'rgba(19, 24, 34, 0.97)',

  sheen:
    'radial-gradient(120% 90% at 0% 0%, rgba(255,255,255,0.10) 0%, ' +
    'rgba(255,255,255,0.03) 30%, rgba(255,255,255,0) 58%)',

  border: '1px solid rgba(255, 255, 255, 0.12)',
  divider: '1px solid rgba(255, 255, 255, 0.10)',

  shadow: '0 34px 70px -22px rgba(0, 0, 0, 0.80)',

  rim:
    'inset 0 1.5px 1px rgba(255, 255, 255, 0.24), ' +
    'inset 0 -1.5px 1px rgba(0, 0, 0, 0.50), ' +
    'inset 3px 0 5px -3px rgba(255, 255, 255, 0.14), ' +
    'inset -3px 0 5px -3px rgba(255, 255, 255, 0.14), ' +
    'inset 0 16px 30px -20px rgba(255, 255, 255, 0.32), ' +
    'inset 0 -20px 34px -22px rgba(0, 0, 0, 0.55)',

  // Blackens first, then takes the brand tint — the mirror of what the light
  // scrim does, and for the same reason: the pane has to land on a ground it
  // chose, not on whatever the page put there.
  backdrop:
    'linear-gradient(rgba(6, 9, 15, 0.62), rgba(6, 9, 15, 0.62)), ' +
    'radial-gradient(120% 110% at 18% 0%, rgba(66, 42, 251, 0.34) 0%, ' +
    'rgba(147, 51, 234, 0.22) 36%, rgba(0, 0, 0, 0.55) 100%)',
  backdropBlur: LIGHT.backdropBlur,

  barFill: 'rgba(8, 11, 18, 0.38)',
  tooltipText: 'rgba(233, 237, 245, 0.94)',
};

/** The token set for a colour mode. */
export const makeGlass = (mode) => (mode === 'dark' ? DARK : LIGHT);

/**
 * The glass for the theme currently in scope.
 *
 * Components used to import `GLASS` directly, which is how they would all
 * have stayed light while everything around them went dark. The theme
 * carries the right set on `theme.glass`; this is the way to reach it.
 */
export const useGlass = () => useTheme().glass || LIGHT;

// Back-compat for anything reading the tokens outside a theme.
export const GLASS = LIGHT;

/**
 * A pinned bar inside a glass panel — a dialog title, a tab strip, an action
 * row. These sit above a scrolling region, and because the panel itself is
 * only 0.12 opaque, content scrolling underneath reads straight through them.
 * A second, local frost fixes that: it blurs whatever passes beneath without
 * turning the bar into the solid slab we started out trying to remove.
 */
export const glassBar = (glass = LIGHT) => ({
  backgroundColor: glass.barFill,
  backdropFilter: 'blur(12px) saturate(180%)',
  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
});

/**
 * Style object for a glass panel.
 *
 * @param {object}  [opts]
 * @param {boolean} [opts.strong]  opaque + heavily blurred, for surfaces that
 *                                 float over live content with no scrim
 * @param {number}  [opts.radius]
 * @param {object}  [opts.glass]   token set — `makeGlass(mode)`
 */
export const glassSurface = ({ strong = false, radius, glass = LIGHT } = {}) => ({
  backgroundColor: strong ? glass.surfaceStrong : glass.surface,
  // Replaces MUI's own elevation overlay, which is a flat wash that fights
  // the translucency and leaves the surface looking milky.
  backgroundImage: glass.sheen,
  backdropFilter: strong ? glass.blurStrong : glass.blur,
  WebkitBackdropFilter: strong ? glass.blurStrong : glass.blur,
  border: glass.border,
  borderRadius: `${radius ?? glass.radius}px`,
  boxShadow: `${glass.shadow}, ${glass.rim}`,

  // Without backdrop-filter the surface is barely tinted glass over raw page
  // content — unreadable. Go nearly opaque instead.
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
    backgroundColor: glass.surfaceFallback,
    backgroundImage: 'none',
  },
});

export default LIGHT;
